// features/automationScripts/components/runResults/StepDetail.js
//
// How one step of a run is drawn — the endpoint, the headers, the request and
// response payloads, what was extracted, and the assertions.
//
// Shared deliberately. The live run panel and the results page have to show a
// step the same way, and the only way to keep that true is for there to be one
// implementation. Both feed it steps put through utils/normaliseRunStep, so
// neither has to know whether they came off the WebSocket or out of the
// summary written to S3.

import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  AlertTriangle, Ban, ChevronDown, CircleCheck, CircleX, Code2, Database, Globe, MinusCircle, Smartphone,
} from 'lucide-react';
import { STEP_TYPE_CONFIG } from '../../config/automationConstants';
import { formatJsonLoose } from '../../utils/formatJsonLoose';
import {
  PERMUTATION_STATUS,
  STEP_RESULT,
  getPermutationStatus,
  getStepResult,
} from '../../../../shared/utils/permutationStatus';
import {
  SELF_EVIDENT_COMPARISONS,
  assertionParts,
  assertionScope,
  isStructured,
  toAssertionView,
} from '../../utils/assertionDisplay';
import { formatRequestLabel, formatStepLabel } from '../../utils/labels';
import '../../styles/RunProgressBar.css';
  const STATUS_BADGE = {
    PASSED:   { className: 'mock-run-details__status-badge--passed',   icon: CircleCheck, label: 'Passed' },
    FAILED:   { className: 'mock-run-details__status-badge--failed',   icon: CircleX,     label: 'Failed' },
    SKIPPED:  { className: 'mock-run-details__status-badge--skipped',  icon: MinusCircle, label: 'Skipped' },
    DISABLED: { className: 'mock-run-details__status-badge--disabled', icon: Ban,         label: 'Disabled' },
  };

  const PENDING_BADGE = { className: 'mock-run-details__status-badge--skipped', icon: MinusCircle, label: 'Pending' };

  // Permutation badge, resolved by status (never by hasPassed — SKIPPED and DISABLED
  // permutations report hasPassed: true).
  const getPermBadge = (perm) => STATUS_BADGE[getPermutationStatus(perm)] || PENDING_BADGE;

  // Step display state. The step's derived `result` (PASS/FAIL/SKIP/DISABLED) drives
  // the badge; endDateTime only distinguishes "still running".
  const getStepState = (step) => {
    if (step.isGroupHeader) return 'header';
    const result = getStepResult(step);
    if (result === STEP_RESULT.PASS)     return 'passed';
    if (result === STEP_RESULT.FAIL)     return 'failed';
    if (result === STEP_RESULT.DISABLED) return 'disabled';
    if (result === STEP_RESULT.SKIP)     return 'skipped';
    return 'in_progress';
  };

  const STEP_BADGE_BY_STATE = {
    passed: STATUS_BADGE.PASSED,
    failed: STATUS_BADGE.FAILED,
    skipped: STATUS_BADGE.SKIPPED,
    disabled: STATUS_BADGE.DISABLED,
  };

  const getStepBadge = (step) => STEP_BADGE_BY_STATE[getStepState(step)] || STATUS_BADGE.SKIPPED;

  /* ── Step Type Icon Helper ──────────────────────────────────────── */
 
  // Backend streams stepType as uppercase (e.g. "MOBILE", "WEB-UI") — normalise for config lookup
  const normaliseStreamStepType = (t) => {
    if (!t) return t;
    const map = { MOBILE: 'Mobile', 'WEB-UI': 'Web-UI', API: 'Api', DATABASE: 'Database' };
    return map[t.toUpperCase()] ?? t;
  };

  const STEP_TYPE_ICON = { Api: Code2, 'Web-UI': Globe, Database, Mobile: Smartphone };
 
  const StepTypeIcon = ({ stepType, size = 10 }) => {
    const normalised = normaliseStreamStepType(stepType);
    const Icon = STEP_TYPE_ICON[normalised];
    const config = STEP_TYPE_CONFIG?.[normalised];
    if (!Icon) return null;
    return <Icon size={size} style={config ? { color: config.color } : undefined} />;
  };
 
  /* ── Group steps by isGroupHeader ──────────────────────────────── */
 
  const groupStepsByHeader = (steps) => {
    if (!steps?.length) return [];
    const groups = [];
    let currentGroup = null;
 
    for (const step of steps) {
      if (step.isGroupHeader) {
        currentGroup = { header: step, actions: [] };
        groups.push(currentGroup);
      } else if (currentGroup) {
        currentGroup.actions.push(step);
      } else {
        // No header yet — treat as standalone step
        groups.push({ header: null, actions: [step] });
      }
    }
    return groups;
  };
 
  /* ── Helpers ───────────────────────────────────────────────────── */

  const formatBytes = (bytes) => {
    if (bytes == null) return null;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  /**
   * Bodies arrive as one long JSON line. Indent them so they read as a
   * document.
   *
   * A body the executor cut short is not valid JSON, so parsing it fails —
   * but that is exactly the body most worth laying out, being long enough to
   * have been cut. So the fallback is a tolerant pass over the structure
   * rather than one unbroken line.
   */
  const formatBody = (raw) => {
    if (typeof raw !== 'string') return raw;
    const trimmed = raw.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return raw;
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return formatJsonLoose(trimmed);
    }
  };

  const EXTRACTION_FAILED = '__EXTRACTION_FAILED__:';

  /**
   * Why an extraction has no value, or null when it does have one.
   *
   * Three shapes, because all three are out there. The executor now sends the
   * diagnosis in place of the value — "not found — searched 'response.x'; the
   * response body has: [...]" — which is the useful one, because it separates
   * *the configured path does not match* (fix the test) from *the API never
   * sent it* (fix the API). Before that it sent an internal marker, and before
   * that a bare null; both are still sitting in summaries already written to
   * S3, so a results page has to keep reading them.
   *
   * Recognising prose is not how this should work — a flag on the payload
   * would settle it — so detection is kept narrow enough that a value which
   * merely reads like a diagnosis is not mistaken for one.
   */
  const extractionFailure = (value) => {
    if (value === null || value === undefined) return 'no value was returned';
    if (typeof value !== 'string') return null;
    if (value.startsWith(EXTRACTION_FAILED)) {
      return value.slice(EXTRACTION_FAILED.length).trim() || 'extraction failed';
    }
    const text = value.trim();
    // The executor's wording: a "not found" opening plus its explanation.
    if (/^not found\b/i.test(text) && text.includes('—')) return text;
    return null;
  };

  const isExtractionFailure = (value) => extractionFailure(value) !== null;

  /**
   * `extractedData` is the executor's whole variable scope, not the variables
   * the user asked for. It repeats both payloads verbatim as
   * requestPayload / responsePayload, and carries the resolved request /
   * response scopes and the CSV rows. All of that is either shown elsewhere on
   * this row or is not the user's data, so it is kept out of "Extracted" —
   * what is left is what they actually asked to extract.
   */
  const SCOPE_KEYS = new Set([
    'request', 'response', 'request_data', 'response_data',
    'requestPayload', 'responsePayload', 'test',
  ]);

  const getUserExtractions = (extractedData) =>
    Object.fromEntries(
      Object.entries(extractedData || {}).filter(([key]) => !SCOPE_KEYS.has(key))
    );

  /**
   * The scopes mix headers in with body fields and with meta that is already
   * on the endpoint line. Authorization and anything hyphenated (content-type,
   * x-*) is a header; the rest is not.
   */
  const NON_HEADER_KEYS = new Set(['method', 'url', 'statusCode', 'responseTime']);

  const isHeaderKey = (key) =>
    key.toLowerCase() === 'authorization' || key.includes('-');

  const entriesOf = (bag) =>
    bag && typeof bag === 'object' ? Object.entries(bag) : [];

  /**
   * The step's headers, from whichever source has them.
   *
   * The summary carries real `requestHeaders` / `responseHeaders` objects, so
   * those are used verbatim — they are the actual headers, unguessed. The
   * stream has no header field at all, so there it falls back to sniffing them
   * out of the resolved request / response scopes.
   */
  const collectHeaderGroups = (step) =>
    [
      { label: 'Request headers', real: step?.requestHeaders, scope: 'request' },
      { label: 'Response headers', real: step?.responseHeaders, scope: 'response' },
    ]
      .map(({ label, real, scope }) => {
        const stated = entriesOf(real);
        if (stated.length) return { label, headers: stated };

        const sniffed = entriesOf(step?.extractedData?.[scope])
          .filter(([key]) => !NON_HEADER_KEYS.has(key) && isHeaderKey(key));
        return sniffed.length ? { label, headers: sniffed } : null;
      })
      .filter(Boolean);

  /* ── Headers ───────────────────────────────────────────────────── */

  /**
   * Headers whose value is a credential. The executor sends these through
   * exactly as the report does, and a run view is the one place a token gets
   * read over someone's shoulder or pasted into a chat, so the secret is
   * covered while the header stays visible.
   */
  const SECRET_HEADERS = new Set([
    'authorization', 'proxy-authorization', 'cookie', 'set-cookie',
    'x-api-key', 'x-auth-token', 'api-key',
  ]);

  /**
   * A credential with its shape intact but its secret gone:
   *   "Bearer eyJraWQi…" → "Bearer •••••••• (912 chars)"
   *
   * The scheme and the length are what someone is actually debugging — whether
   * a token was sent at all, and whether it looks the right size. The bytes
   * are not.
   */
  const maskSecret = (value) => {
    const text = String(value);
    const scheme = text.match(/^(Bearer|Basic|Digest|Token)\s+/i);
    const covered = `•••••••• (${text.length} chars)`;
    return scheme ? `${scheme[1]} ${covered}` : covered;
  };

  const isSecretHeader = (key) => SECRET_HEADERS.has(String(key).toLowerCase());

  const HeadersBlock = ({ groups }) => (
<div className="mock-run-details__headers" data-testid="step-block-headers">
      {groups.map(({ label, headers }) => (
<div key={label} className="mock-run-details__header-group">
<div className="mock-run-details__body-label"><span>{label}</span></div>
<dl className="mock-run-details__extract-list">
            {headers.map(([key, value]) => (
<React.Fragment key={key}>
<dt className="mock-run-details__extract-key">{key}</dt>
<dd className="mock-run-details__extract-val" data-testid={`header-val-${String(key).toLowerCase()}`}>
                  {value === '' || value == null
                    ? <span className="mock-run-details__empty-val">empty</span>
                    : (isSecretHeader(key)
                      ? <span className="mock-run-details__masked-val">{maskSecret(value)}</span>
                      : formatExtractedValue(value))}
</dd>
</React.Fragment>
            ))}
</dl>
</div>
      ))}
</div>
  );

  HeadersBlock.propTypes = { groups: PropTypes.array.isRequired };

  /**
   * The executor sends whole objects for the request / response / request_data
   * scopes, so String() on them reads "[object Object]".
   */
  const formatExtractedValue = (value) => {
    if (value == null) return String(value);
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  /** The reason a FAILED permutation failed, or null for any other status. */
  const getPermFailureText = (perm) => {
    if (getPermutationStatus(perm) !== PERMUTATION_STATUS.FAILED) return null;
    const failedStep = perm.steps?.find((s) => getStepResult(s) === STEP_RESULT.FAIL);
    return failedStep?.assertionResponse?.results?.[0] || failedStep?.failureReason || null;
  };

  /* ── Request / response body block ─────────────────────────────── */

  /**
   * The body is shown whole, wrapped rather than clipped — a long token is
   * still something you might need to read, and nothing here editorialises
   * about it. Where the executor itself cut a body short it says so inside the
   * text it sent, which is the honest place for that to be said.
   */
  const BodyBlock = ({ label, raw, size }) => (
<div className="mock-run-details__body">
<div className="mock-run-details__body-label">
<span>{label}</span>
        {size != null && <span className="mock-run-details__body-size">{formatBytes(size)}</span>}
</div>
<pre className="mock-run-details__body-content" data-testid="body-content">{formatBody(raw)}</pre>
</div>
  );

  BodyBlock.propTypes = {
    label: PropTypes.string.isRequired,
    raw: PropTypes.string.isRequired,
    size: PropTypes.number,
  };
  BodyBlock.defaultProps = { size: null };

  /* ── Extracted variables ───────────────────────────────────────── */

  /**
   * One row per variable rather than a wrap of pills: the values are JSON
   * objects and URLs, which a pill either truncates to uselessness or lets
   * push the row off screen. Failed extractions are pulled out to the top —
   * they are the reason someone opens this section.
   */
  const ExtractedList = ({ data }) => {
    const entries = Object.entries(data);
    const failures = entries.filter(([, v]) => isExtractionFailure(v));
    const values = entries.filter(([, v]) => !isExtractionFailure(v));

    return (
<div className="mock-run-details__extracted" data-testid="step-block-extracted">
<div className="mock-run-details__extracted-title">
          Extracted
<span className="mock-run-details__extracted-count" data-testid="extracted-count">{entries.length}</span>
</div>

        {failures.length > 0 && (
<div className="mock-run-details__extract-fails">
            {failures.map(([key, value]) => (
<div key={key} className="mock-run-details__extract-fail" data-testid="extract-failure">
<AlertTriangle size={11} />
<span className="mock-run-details__extract-fail-key">{key}</span>
<span className="mock-run-details__extract-fail-msg">
                  {extractionFailure(value)}
</span>
</div>
            ))}
</div>
        )}

        {values.length > 0 && (
<dl className="mock-run-details__extract-list">
            {values.map(([key, value]) => {
              const text = formatExtractedValue(value);
              const isBlock = text.includes('\n');
              return (
<React.Fragment key={key}>
<dt className="mock-run-details__extract-key">{key}</dt>
<dd className={`mock-run-details__extract-val${isBlock ? ' mock-run-details__extract-val--block' : ''}`}>
                    {isBlock ? <pre>{text}</pre> : text}
</dd>
</React.Fragment>
              );
            })}
</dl>
        )}
</div>
    );
  };

  ExtractedList.propTypes = { data: PropTypes.object.isRequired };

  /* ── Assertions ────────────────────────────────────────────────── */

  /** One value of an assertion, labelled: `expected  true`. */
  const AssertionValue = ({ label, value, tone }) => (
<span className={`mock-run-details__assertion-value mock-run-details__assertion-value--${tone}`}>
<em>{label}</em>
<code>{value == null || value === '' ? '—' : String(value)}</code>
</span>
  );

  AssertionValue.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.any,
    tone: PropTypes.string.isRequired,
  };
  AssertionValue.defaultProps = { value: null };

  /**
   * Assertions are what the run is for, so each one is one padded line that
   * reads left to right: what was compared, the two values, the verdict. A
   * result the stream sent in a shape we cannot take apart still shows its
   * text — better a terse sentence than a confidently wrong split.
   */
  const AssertionsBlock = ({ assertionResponse }) => {
    const rows = assertionResponse.results.map((result, i) => ({
      key: i,
      view: toAssertionView(result, assertionResponse.passed),
    }));

    const passedCount = rows.filter((r) => r.view.passed).length;
    const allPassed = passedCount === rows.length;

    return (
<div
        className={`mock-run-details__assertions${allPassed ? '' : ' mock-run-details__assertions--failed'}`}
        data-testid="step-block-assertions"
        data-all-passed={allPassed}
>
<div className="mock-run-details__assertions-title">
          {allPassed ? <CircleCheck size={12} /> : <AlertTriangle size={12} />}
<span>Assertions</span>
<span className="mock-run-details__assertions-tally">
            {passedCount}/{rows.length} passed
</span>
</div>

        {rows.map(({ key, view }) => {
          const laidOut = isStructured(view);
          // NULL / NOT_NULL / TRUE / FALSE are their own expectation, so there
          // is no expected value to put beside the actual one.
          const selfEvident = SELF_EVIDENT_COMPARISONS.has(view.comparison);
          return (
<div
              key={key}
              className={`mock-run-details__assertion${view.passed ? '' : ' mock-run-details__assertion--failed'}`}
              data-testid="assertion"
              data-passed={view.passed}
>
<span className="mock-run-details__assertion-icon">
                {view.passed ? <CircleCheck size={13} /> : <CircleX size={13} />}
</span>

              {laidOut ? (
<>
<span
                    className="mock-run-details__assertion-stem"
                    title={view.isKnownComparison ? `Comparison: ${view.comparison}` : undefined}
>
{/* The response value being checked is the subject; the
                        comparison is only the connective, so it is weighted
                        down to keep them apart at a glance. */}
<span className="mock-run-details__assertion-subject">
                      {assertionParts(view).subject}
</span>
                    {' '}
<span className="mock-run-details__assertion-op">
                      {assertionParts(view).phrase}
</span>
                    {/* Database assertions can cover more than one row; which
                        rows they covered is the difference between two checks
                        that otherwise read identically. */}
                    {assertionScope(view) && (
<>
                        {' '}
<span className="mock-run-details__assertion-scope">
                          {assertionScope(view)}
</span>
</>
                    )}
</span>
<div className={`mock-run-details__assertion-values${selfEvident ? ' mock-run-details__assertion-values--single' : ''}`}>
                    {!selfEvident && (
<AssertionValue label="expected" value={view.expected} tone="expected" />
                    )}
<AssertionValue
                      label="actual"
                      value={view.actual}
                      tone={view.passed ? 'actual' : 'actual-failed'}
                    />
</div>
</>
              ) : (
<span className="mock-run-details__assertion-stem">{view.text}</span>
              )}

<span className="mock-run-details__assertion-status">
                {view.passed ? 'Passed' : 'Failed'}
</span>
</div>
          );
        })}
</div>
    );
  };

  AssertionsBlock.propTypes = { assertionResponse: PropTypes.object.isRequired };

  /* ── Inline Step Detail Row ────────────────────────────────────── */

  const StepDetailRow = ({ step }) => {
    const badge = getStepBadge(step);
    const BadgeIcon = badge.icon;
    const normType = normaliseStreamStepType(step.stepType);
    const isDb = normType === 'Database';
    const method = step.httpMethod || step.httpMethods;
    // Only a FAIL result tints the row red — a skipped or disabled step is neither
    // a pass nor a failure (and both report hasPassed: true).
    const state = getStepState(step);
    const headerGroups = collectHeaderGroups(step);
    const extractions = getUserExtractions(step.extractedData);
    const requestLabel = formatRequestLabel(step.alias);

    return (
<div className={[
        'mock-run-details__inline-step',
        state === 'failed' ? 'mock-run-details__inline-step--failed' : '',
        state === 'skipped' || state === 'disabled' ? 'mock-run-details__inline-step--inert' : '',
      ].filter(Boolean).join(' ')}>
        {/* Header — the request named readably, with the alias kept beside it
            because that is what a variable reference has to be written as. */}
<div className="mock-run-details__inline-step-header">
<StepTypeIcon stepType={step.stepType} size={13} />
<span className="mock-run-details__inline-step-alias">
            {requestLabel || step.name}
</span>
          {step.alias && (
<code className="mock-run-details__inline-step-ref">{step.alias}</code>
          )}
<span className={`mock-run-details__status-badge ${badge.className}`}>
<BadgeIcon size={10} />
            {badge.label}
</span>
          {step.duration && (
<span className="mock-run-details__duration">{step.duration}</span>
          )}
</div>

        {/* Endpoint — the URL gets a line of its own so it is readable in full
            rather than ellipsed at 300px, with the method and status code
            beside it as labelled facts. */}
        {step.requestUrl && (
<div className="mock-run-details__endpoint">
<div className="mock-run-details__endpoint-line">
              {method && <span className="mock-run-details__method">{method}</span>}
              {step.responseStatusCode > 0 && (
<span className={`mock-run-details__code${step.responseStatusCode >= 400 ? ' mock-run-details__code--error' : ''}`}>
                  {step.responseStatusCode}
</span>
              )}
              {isDb ? <Database size={11} /> : <Globe size={11} />}
<span className="mock-run-details__url">{step.requestUrl}</span>
</div>
</div>
        )}

        {/* DB: row + column count */}
        {isDb && (step.rowCount != null || step.columnCount != null) && (
<div className="mock-run-details__step-db-meta">
            {step.rowCount != null && <span>{step.rowCount} row{step.rowCount !== 1 ? 's' : ''}</span>}
            {step.columnCount != null && <span>{step.columnCount} col{step.columnCount !== 1 ? 's' : ''}</span>}
</div>
        )}

        {/* Headers, where the stream carries any. */}
        {headerGroups.length > 0 && <HeadersBlock groups={headerGroups} />}

        {/* The payloads, stacked and each labelled, shown in full. */}
        {(step.requestBody || step.responseBody) && (
<div className="mock-run-details__bodies" data-testid="step-block-bodies">
            {step.requestBody && (
<BodyBlock label={isDb ? 'Query' : 'Request payload'} raw={step.requestBody} />
            )}
            {step.responseBody && (
<BodyBlock
                label={isDb ? 'Result' : 'Response payload'}
                raw={step.responseBody}
                size={step.responseSizeBytes}
              />
            )}
</div>
        )}

        {/* What the user asked to extract — the executor's internal scopes are
            filtered out, so a failed extraction is not buried under copies of
            the payloads. */}
        {Object.keys(extractions).length > 0 && <ExtractedList data={extractions} />}

        {/* Failure reason */}
        {step.failureReason && (
<div className="mock-run-details__step-error">
<AlertTriangle size={11} />
            {step.failureReason}
</div>
        )}

        {/* Assertions — the reason the step exists, so they close the row in a
            block of their own with a tally, whether they passed or failed. */}
        {step.assertionResponse?.results?.length > 0 && (
<AssertionsBlock assertionResponse={step.assertionResponse} />
        )}
</div>
    );
  };
 
  /* ── Permutation steps ─────────────────────────────────────────── */

  /** What a step header says, with or without a control wrapped round it. */
  const StepGroupLabel = ({ header, count, hasFailure, showFailure }) => (
<>
<span className="mock-run-details__step-number">
        {formatStepLabel(header.alias)}
</span>
<StepTypeIcon stepType={header.stepType} size={13} />
<span className="mock-run-details__step-group-label">
        {header.name || header.alias}
</span>
      {showFailure && hasFailure && (
<span className="mock-run-details__step-group-failed">
<AlertTriangle size={11} />
          Failed
</span>
      )}
<span className="mock-run-details__step-group-count">
        {count} request{count === 1 ? '' : 's'}
</span>
</>
  );

  StepGroupLabel.propTypes = {
    header: PropTypes.object.isRequired,
    count: PropTypes.number.isRequired,
    hasFailure: PropTypes.bool,
    showFailure: PropTypes.bool,
  };
  StepGroupLabel.defaultProps = { hasFailure: false, showFailure: false };

  /**
   * The steps of one permutation.
   *
   * `collapsible` is off by default: while a run streams you want everything
   * on screen as it happens, and the permutation accordion is already doing
   * the job of keeping that readable. A summary is the opposite — it is a
   * finished record you scan, so the results page turns this on and the steps
   * fold away until asked for.
   */
  const PermutationSteps = ({ perm, collapsible }) => {
    const stepGroups = useMemo(() => groupStepsByHeader(perm.steps), [perm.steps]);

    // Collapsed steps start closed except one that failed, which opens itself —
    // the thing you came to look at should not need finding.
    //
    // The default is derived, not seeded into state, so a step that fails part
    // way through opens as soon as it does. `overrides` records only the groups
    // the user has actually clicked, and those always win: a step they closed
    // stays closed even if a later request in it fails.
    const [overrides, setOverrides] = useState({});

    const toggle = useCallback((key, openByDefault) => {
      setOverrides((prev) => ({
        ...prev,
        [key]: !(key in prev ? prev[key] : openByDefault),
      }));
    }, []);

    return (
<div className="mock-run-details__expanded-steps">
        {stepGroups.length > 0 ? stepGroups.map((group, gIdx) => {
          const key = group.header?.alias || `group-${gIdx}`;
          const count = group.actions.length;
          // 'failed' is the same signal that tints the row red, so a step that
          // looks like a problem is always a step that opened itself.
          const hasFailure = group.actions.some((s) => getStepState(s) === 'failed');
          const isOpen = !collapsible
            || (key in overrides ? overrides[key] : hasFailure);

          return (
<div key={key} className="mock-run-details__step-group">
              {group.header && (collapsible ? (
<button
                  type="button"
                  className={`mock-run-details__step-group-header-row${hasFailure ? ' mock-run-details__step-group-header-row--failed' : ''}`}
                  data-testid="step-group-header"
                  aria-expanded={isOpen}
                  onClick={() => toggle(key, hasFailure)}
>
<ChevronDown
                    size={13}
                    className={`mock-run-details__step-group-chevron${isOpen ? ' mock-run-details__step-group-chevron--open' : ''}`}
                  />
<StepGroupLabel header={group.header} count={count} hasFailure={hasFailure} showFailure />
</button>
              ) : (
<div
                  className="mock-run-details__step-group-header-row mock-run-details__step-group-header-row--static"
                  data-testid="step-group-header"
>
<StepGroupLabel header={group.header} count={count} />
</div>
              ))}
              {/* A group with no header has nothing to toggle, so it always shows. */}
              {(!group.header || isOpen) && group.actions.map((step, sIdx) => (
<StepDetailRow key={sIdx} step={step} />
              ))}
</div>
          );
        }) : (
          perm.steps?.map((step, sIdx) => (
<StepDetailRow key={sIdx} step={step} />
          ))
        )}
</div>
    );
  };

  PermutationSteps.propTypes = {
    perm: PropTypes.object.isRequired,
    collapsible: PropTypes.bool,
  };
  PermutationSteps.defaultProps = { collapsible: false };


/* ── Shared surface ────────────────────────────────────────────── */

export {
  STATUS_BADGE,
  PENDING_BADGE,
  getPermBadge,
  getStepState,
  getStepBadge,
  getPermFailureText,
  normaliseStreamStepType,
  StepTypeIcon,
  groupStepsByHeader,
  PermutationSteps,
};

export default StepDetailRow;
